import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, TYPE_META, fmtDate } from "@/lib/brand";
import { CLOUD_LOGO } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { FileText, FileSpreadsheet, Filter, Clock, Timer, Ticket as TicketIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ALL = "all";

export default function Reports() {
  const { user } = useAuth();
  const isClient = user.role === "client";
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({
    client_id: ALL, status: ALL, priority: ALL, type: ALL, start: "", end: "",
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = () => {
    const p = {};
    ["client_id", "status", "priority", "type"].forEach((k) => {
      if (filters[k] && filters[k] !== ALL) p[k === "type" ? "type" : k] = filters[k];
    });
    if (filters.start) p.start = filters.start;
    if (filters.end) p.end = filters.end;
    return p;
  };

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/tickets", { params: params() });
      setReport(r.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isClient) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/tickets/export.csv", { params: params(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ticket-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = () => {
    if (!report) return;
    const s = report.summary;
    const clientName = isClient ? user.client?.name : (filters.client_id !== ALL ? clients.find((c) => c.id === filters.client_id)?.name : "All Clients");
    const row = (r) => `<tr>
      <td>${escapeHtml(r.title)}</td><td>${escapeHtml(r.client_name)}</td>
      <td>${STATUS_META[r.status]?.label || r.status}</td>
      <td>${PRIORITY_META[r.priority]?.label || r.priority}</td>
      <td>${TYPE_META[r.type] || r.type}</td>
      <td>${escapeHtml(r.assignee_name || "—")}</td>
      <td>${r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
      <td>${r.resolution_hours != null ? r.resolution_hours + "h" : "—"}</td>
    </tr>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Ticket Report</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a;margin:0;padding:40px}
        header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #F7941E;padding-bottom:16px;margin-bottom:24px}
        header img{height:40px}
        h1{font-size:22px;margin:0} .sub{color:#64748b;font-size:12px;margin-top:4px}
        .cards{display:flex;gap:12px;margin:20px 0}
        .card{flex:1;border:1px solid #e2e8f0;border-radius:8px;padding:14px}
        .card .n{font-size:26px;font-weight:700} .card .l{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#64748b}
        h2{font-size:14px;text-transform:uppercase;letter-spacing:.06em;color:#334155;margin:28px 0 10px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{text-align:left;background:#f1f5f9;padding:8px;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#475569}
        td{padding:8px;border-bottom:1px solid #eef2f6}
        .foot{margin-top:30px;font-size:10px;color:#94a3b8;text-align:center}
      </style></head><body>
      <header>
        <div><h1>Ticket Report</h1><div class="sub">${escapeHtml(clientName || "All Clients")} · Generated ${new Date().toLocaleString()}</div></div>
        <img src="${CLOUD_LOGO}" alt="CloudPoweredIT"/>
      </header>
      <div class="cards">
        <div class="card"><div class="n">${s.total}</div><div class="l">Total Tickets</div></div>
        <div class="card"><div class="n">${s.open}</div><div class="l">Open</div></div>
        <div class="card"><div class="n">${s.resolved}</div><div class="l">Resolved</div></div>
        <div class="card"><div class="n">${s.avg_resolution_hours != null ? s.avg_resolution_hours + "h" : "—"}</div><div class="l">Avg Resolution</div></div>
        <div class="card"><div class="n">${s.avg_first_response_hours != null ? s.avg_first_response_hours + "h" : "—"}</div><div class="l">Avg First Response</div></div>
      </div>
      <h2>Tickets (${report.tickets.length})</h2>
      <table><thead><tr><th>Title</th><th>Client</th><th>Status</th><th>Priority</th><th>Type</th><th>Assignee</th><th>Created</th><th>Resolution</th></tr></thead>
      <tbody>${report.tickets.map(row).join("")}</tbody></table>
      <div class="foot">CloudPoweredIT Client Portal · Confidential</div>
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) {
      URL.revokeObjectURL(url);
      return toast.error("Allow pop-ups to export PDF");
    }
    w.addEventListener("load", () => {
      w.focus();
      w.print();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  };

  const s = report?.summary;
  const statusData = s ? STATUS_ORDER.map((k) => ({ name: STATUS_META[k].label, value: s.by_status[k] || 0, fill: STATUS_META[k].dot })) : [];
  const priorityData = s ? Object.entries(s.by_priority).map(([k, v]) => ({ name: PRIORITY_META[k].label, value: v, fill: PRIORITY_META[k].color })) : [];
  const typeData = s ? Object.entries(s.by_type).map(([k, v]) => ({ name: TYPE_META[k] || k, value: v })) : [];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Ticketing Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Generate, filter and export ticket performance reports.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={exporting || !report} data-testid="export-csv-btn">
            <FileSpreadsheet size={16} className="mr-1.5" /> CSV
          </Button>
          <Button className="text-white" style={{ background: "var(--tenant-primary)" }} onClick={exportPdf} disabled={!report} data-testid="export-pdf-btn">
            <FileText size={16} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center gap-2 mb-3 text-slate-700">
          <Filter size={15} /><span className="text-sm font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {!isClient && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Client</Label>
              <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="filter-client"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All clients</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {STATUS_ORDER.map((k) => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Priority</Label>
            <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All priorities</SelectItem>
                {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Type</Label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">From</Label>
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="mt-1 h-9" data-testid="filter-start" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">To</Label>
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="mt-1 h-9" data-testid="filter-end" />
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={generate} disabled={loading} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="generate-report-btn">
            {loading ? "Generating…" : "Generate Report"}
          </Button>
        </div>
      </Card>

      {s && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="report-summary">
            <MiniStat label="Total" value={s.total} icon={TicketIcon} accent="#0E4C7A" />
            <MiniStat label="Open" value={s.open} icon={Clock} accent="#3B82F6" />
            <MiniStat label="Resolved" value={s.resolved} icon={CheckCircle2} accent="#10B981" />
            <MiniStat label="Avg Resolution" value={s.avg_resolution_hours != null ? `${s.avg_resolution_hours}h` : "—"} icon={Timer} accent="#F7941E" />
            <MiniStat label="Avg 1st Response" value={s.avg_first_response_hours != null ? `${s.avg_first_response_hours}h` : "—"} icon={Timer} accent="#8B5CF6" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-5 lg:col-span-2 border-slate-200">
              <p className="font-display font-semibold text-slate-900 mb-4">By Status</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={statusData} barCategoryGap={28}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {statusData.map((d) => <Cell key={d.name} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-slate-200">
              <p className="font-display font-semibold text-slate-900 mb-4">By Priority</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={priorityData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {priorityData.map((d) => <Cell key={d.name} fill={d.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 justify-center">
                {priorityData.map((d) => (
                  <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />{d.name}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {!isClient && report.per_client.length > 0 && (
            <Card className="border-slate-200 overflow-hidden">
              <p className="font-display font-semibold text-slate-900 p-5 pb-3">Per-Client Breakdown</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-2.5">Client</th>
                      <th className="text-right px-4 py-2.5">Total</th>
                      <th className="text-right px-4 py-2.5">Open</th>
                      <th className="text-right px-4 py-2.5">Resolved</th>
                      <th className="text-right px-5 py-2.5">Avg Resolution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.per_client.map((c) => (
                      <tr key={c.client_name} data-testid="per-client-row" className="hover:bg-slate-50">
                        <td className="px-5 py-2.5 text-slate-800 font-medium">{c.client_name}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{c.total}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{c.open}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{c.resolved}</td>
                        <td className="px-5 py-2.5 text-right text-slate-600">{c.avg_resolution_hours != null ? `${c.avg_resolution_hours}h` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Card className="border-slate-200 overflow-hidden">
            <p className="font-display font-semibold text-slate-900 p-5 pb-3">Tickets ({report.tickets.length})</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-2.5">Title</th>
                    {!isClient && <th className="text-left px-4 py-2.5">Client</th>}
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5">Priority</th>
                    <th className="text-left px-4 py-2.5">Type</th>
                    <th className="text-left px-4 py-2.5">Created</th>
                    <th className="text-right px-5 py-2.5">Resolution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.tickets.map((t) => (
                    <tr key={t.id} data-testid="report-ticket-row" className="hover:bg-slate-50">
                      <td className="px-5 py-2.5 text-slate-800 max-w-[280px] truncate">{t.title}</td>
                      {!isClient && <td className="px-4 py-2.5 text-slate-600">{t.client_name}</td>}
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_META[t.status]?.bg} ${STATUS_META[t.status]?.text}`}>{STATUS_META[t.status]?.label}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${PRIORITY_META[t.priority]?.bg}`}>{PRIORITY_META[t.priority]?.label}</span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{TYPE_META[t.type]}</td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(t.created_at)}</td>
                      <td className="px-5 py-2.5 text-right text-slate-600">{t.resolution_hours != null ? `${t.resolution_hours}h` : "—"}</td>
                    </tr>
                  ))}
                  {report.tickets.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10">No tickets match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-4 border-slate-200 flex items-center justify-between">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-0.5">{value}</p>
      </div>
      <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${accent}1a` }}>
        <Icon size={18} style={{ color: accent }} />
      </div>
    </Card>
  );
}

function escapeHtml(str = "") {
  return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
